import React from "react";
import styled, { keyframes } from "styled-components";
import { SP_BREAKPOINT } from "../../../constants/app";
import { ModalDataItem } from "../../../ducks/app";
import { Button } from "../../atoms/Button";
import { CloseIcon } from "../../atoms/icons/CloseIcon";

type Props = ModalDataItem & {
  isOpen: boolean;
  defaultCloseModal?: () => void;
};

export const PopupModal: React.FC<Props> = ({
  title,
  buttons,
  isOpen,
  defaultCloseModal,
}) => {
  if (!isOpen) return null;

  return (
    <StyledModalBackground $isOpen={isOpen}>
      <StyledModalContainer $isOpen={isOpen}>
        <StyledHeader>
          <StyledModalTitle>{title}</StyledModalTitle>

          <StyledCloseButton onClick={defaultCloseModal}>
            <CloseIcon />
          </StyledCloseButton>
        </StyledHeader>

        <StyledFooter>
          {buttons.map((button, index) => (
            <Button
              key={index}
              text={button.text}
              variant={button.variant}
              onClick={button.onClick ?? defaultCloseModal}
            />
          ))}
        </StyledFooter>
      </StyledModalContainer>
    </StyledModalBackground>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9, 0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1, 1);
  }
`;

const StyledModalBackground = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9998;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: all 0.5s ease;
`;

const StyledModalContainer = styled.div<{ $isOpen: boolean }>`
  @media (max-width: ${SP_BREAKPOINT}) {
    width: calc(100% - 40px);
  }

  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  max-width: 512px;
  padding: 28px 32px;
  border-radius: 8px;
  ${({ $isOpen }) => ($isOpen ? "" : "display: none;")}
  background-color: white;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  box-shadow: 4px 6px 10px 0px rgba(0, 0, 0, 0.1);
  transform: ${({ $isOpen }) =>
    $isOpen
      ? "translate(-50%, -50%) scale(1, 1)"
      : "translate(-50%, -50%) scale(0.9, 0.9)"};
  animation: ${fadeIn} 0.5s forwards;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const StyledModalTitle = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    max-width: 100%;
  }

  max-width: 390px;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-line;
`;

const StyledCloseButton = styled.div`
  position: absolute;
  top: 9px;
  right: 12px;
  cursor: pointer;
`;

const StyledFooter = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    justify-content: center;

    button {
      width: 100%;
    }
  }

  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;
